import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, FileEdit, X } from 'lucide-react';
import { api } from '../api/client';
import { toast } from '../lib/toast';

// C1: AutoPilot auto-rewrite approval queue. Lists rewrites AutoPilot
// generated for slipped pages; Approve applies the rewrite to the page,
// Discard drops it. Nothing publishes to the live site automatically.
export default function PendingRewrites({ domain }) {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ['pending-rewrites', domain._id],
    queryFn: () =>
      api
        .get(`/api/domains/${domain._id}/autopilot/pending-rewrites`)
        .then((r) => r.data.pendingRewrites),
    retry: false,
  });

  const decide = useMutation({
    mutationFn: ({ id, action }) =>
      api
        .post(`/api/domains/${domain._id}/autopilot/pending-rewrites/${id}/${action}`)
        .then((r) => r.data),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['pending-rewrites', domain._id] });
      toast.success(vars.action === 'approve' ? 'Rewrite approved' : 'Rewrite discarded');
    },
    onError: (err) => toast.error(err, 'Could not update rewrite'),
  });

  if (query.isError) return null; // gated out (no AutoPilot)
  if (query.isLoading) return null;

  const rows = query.data || [];

  return (
    <div className="card">
      <div className="section-header">
        <div className="section-title">
          <span className="section-icon">
            <FileEdit className="w-4 h-4" />
          </span>
          Rewrites to review
          {rows.length > 0 && (
            <span className="pill bg-amber-50 text-amber-700 ml-1">{rows.length}</span>
          )}
        </div>
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-ink-500">
          No rewrites waiting. When AutoPilot finds a page whose score slipped, it generates a
          fresh rewrite in your brand voice and queues it here for your approval — nothing goes
          live until you approve it.
        </p>
      ) : (
        <ul className="space-y-2">
          {rows.map((r) => (
            <li
              key={r._id}
              className="flex items-center justify-between gap-3 border border-gray-100 rounded-lg px-3 py-2"
            >
              <div className="min-w-0">
                <div className="text-sm font-medium text-ink-800 truncate">
                  {r.page_title || r.page_url}
                </div>
                <div className="text-xs text-ink-500 truncate">{r.page_url}</div>
                {typeof r.prev_score === 'number' && typeof r.new_score === 'number' && (
                  <div className="text-xs text-rose-600 mt-0.5">
                    Score slipped {r.prev_score} → {r.new_score}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  type="button"
                  className="btn-primary text-xs"
                  disabled={decide.isPending}
                  onClick={() => decide.mutate({ id: r._id, action: 'approve' })}
                >
                  <Check className="w-3.5 h-3.5" /> Approve
                </button>
                <button
                  type="button"
                  className="btn-ghost text-xs"
                  disabled={decide.isPending}
                  onClick={() => decide.mutate({ id: r._id, action: 'discard' })}
                >
                  <X className="w-3.5 h-3.5" /> Discard
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
